import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Web, Inviter, UserAgent, SessionState } from 'sip.js';
import toast from 'react-hot-toast';
import api from '../services/api';
import { pbxWS } from '../services/websocket';

const SoftphoneContext = createContext(null);

function getWsServer() {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws/sip`;
}

export function SoftphoneProvider({ children }) {
  const [regState, setRegState] = useState('unregistered');
  const [callState, setCallState] = useState('idle');
  const [currentCall, setCurrentCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [credentials, setCredentials] = useState(null);
  // Credencial TURN temporal del backend: undefined = pidiéndola · null = no disponible (solo STUN) · {username, credential}
  const [turnCred, setTurnCred] = useState(undefined);
  const [callProgress, setCallProgress] = useState(null); // { disposition, statusText, final }
  // Conferencia 3 vías: segunda llamada saliente + mezcla de audio local.
  // secondCall: { number, state: 'calling'|'active' } | null
  const [secondCall, setSecondCall] = useState(null);

  const simpleUserRef = useRef(null);
  const secondSessionRef = useRef(null);
  const audioMixRef = useRef(null); // { ctx, callAOriginalTrack, callASender, callBSender }
  const remoteAudioRef = useRef(new Audio());
  remoteAudioRef.current.autoplay = true;

  // ── Selección de hardware de audio (micrófono / altavoz) ──────────
  // Detecta los dispositivos conectados al PC y permite elegir cuál usar.
  // La causa #1 de "el remoto no oye al agente" era el mic por defecto mal
  // (sin permiso / dispositivo equivocado / silenciado). El deviceId elegido
  // se guarda en localStorage y se aplica a getUserMedia (mic) y setSinkId (altavoz).
  const [audioDevices, setAudioDevices] = useState({ mics: [], speakers: [] });
  const [selectedMic, setSelectedMicState] = useState(() => { try { return localStorage.getItem('pbx-mic-id') || ''; } catch { return ''; } });
  const [selectedSpeaker, setSelectedSpeakerState] = useState(() => { try { return localStorage.getItem('pbx-speaker-id') || ''; } catch { return ''; } });
  const [micLevel, setMicLevel] = useState(0);
  const [audioError, setAudioError] = useState('');   // '' = ok; texto = problema de hardware
  // refs para que los closures de SimpleUser (creado una sola vez) lean el valor ACTUAL
  const selectedMicRef = useRef(selectedMic);
  const selectedSpeakerRef = useRef(selectedSpeaker);
  const micTestRef = useRef(null);

  // ── Procesamiento de audio (ajustes del agente) ────────────────────
  // Cancelación de ruido/eco y control automático de ganancia = constraints
  // NATIVAS de getUserMedia (el navegador hace el DSP; no reinventamos nada).
  // Volumen de mic = ganancia manual con Web Audio; en 100% NO se toca la pista
  // (cero riesgo para el audio por defecto). Volumen de altavoz = element.volume.
  // Todo persiste en localStorage.
  const lsBool = (k, def) => { try { const v = localStorage.getItem(k); return v === null ? def : v === '1'; } catch { return def; } };
  const lsNum = (k, def) => { try { const v = parseInt(localStorage.getItem(k), 10); return Number.isFinite(v) ? v : def; } catch { return def; } };
  const [noiseSuppression, setNoiseState] = useState(() => lsBool('pbx-ns', true));
  const [echoCancellation, setEchoState] = useState(() => lsBool('pbx-ec', true));
  const [autoGainControl, setAgcState] = useState(() => lsBool('pbx-agc', true));
  const [micGain, setMicGainState] = useState(() => lsNum('pbx-mic-gain', 100));        // 0-200 %, 100 = sin cambios
  const [speakerVolume, setSpeakerVolState] = useState(() => lsNum('pbx-spk-vol', 100)); // 0-100 %
  const noiseRef = useRef(noiseSuppression);
  const echoRef = useRef(echoCancellation);
  const agcRef = useRef(autoGainControl);
  const micGainRef = useRef(micGain);
  const speakerVolRef = useRef(speakerVolume);
  const micProcRef = useRef(null); // grafo de ganancia activo: { ctx, gainNode, dest, rawTrack }

  // Constrains de getUserMedia con el mic elegido + procesamiento nativo. `ideal`
  // (no `exact`) para que, si el dispositivo se desconecta, caiga al predeterminado
  // sin romper la llamada.
  const buildConstraints = () => {
    const a = {
      echoCancellation: echoRef.current,
      noiseSuppression: noiseRef.current,
      autoGainControl: agcRef.current,
    };
    if (selectedMicRef.current) a.deviceId = { ideal: selectedMicRef.current };
    return { audio: a, video: false };
  };

  const applySinkId = useCallback((id) => {
    const el = remoteAudioRef.current;
    if (el && typeof el.setSinkId === 'function' && id) {
      el.setSinkId(id).catch((e) => console.warn('[Softphone] setSinkId', e));
    }
  }, []);

  const setSelectedMic = useCallback((id) => {
    selectedMicRef.current = id || '';
    setSelectedMicState(id || '');
    try { id ? localStorage.setItem('pbx-mic-id', id) : localStorage.removeItem('pbx-mic-id'); } catch {}
  }, []);

  const setSelectedSpeaker = useCallback((id) => {
    selectedSpeakerRef.current = id || '';
    setSelectedSpeakerState(id || '');
    try { id ? localStorage.setItem('pbx-speaker-id', id) : localStorage.removeItem('pbx-speaker-id'); } catch {}
    applySinkId(id);
  }, [applySinkId]);

  // Enumerar dispositivos + primar permiso de micrófono (sin permiso las etiquetas
  // vienen vacías, y además garantiza que el permiso esté concedido).
  useEffect(() => {
    let mounted = true;
    let permWarn = '';
    const enumerate = async () => {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        if (!mounted) return;
        const mics = list.filter((d) => d.kind === 'audioinput');
        const speakers = list.filter((d) => d.kind === 'audiooutput');
        setAudioDevices({ mics, speakers });
        // Reglas de error de hardware (petición del usuario): sin mic o sin salida = error.
        let err = permWarn;
        if (!err && mics.length === 0) err = 'No se detectó ningún MICRÓFONO conectado. Conecta unos auriculares/diadema con micrófono y recarga.';
        else if (!err && speakers.length === 0) err = 'No se detectó ningún ALTAVOZ/AURICULAR de salida. Conecta un dispositivo de audio y recarga.';
        setAudioError(err);
        if (err) { try { toast.error(err, { id: 'audio-hw', duration: 8000 }); } catch {} }
      } catch (e) { console.warn('[Softphone] enumerateDevices', e); }
    };
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach((t) => t.stop());
      } catch (e) {
        console.warn('[Softphone] permiso de micrófono no concedido', e);
        permWarn = (e && (e.name === 'NotAllowedError' || e.name === 'SecurityError'))
          ? 'Micrófono BLOQUEADO en el navegador. Haz clic en el candado de la barra de direcciones → Micrófono → Permitir, y recarga.'
          : (e && e.name === 'NotFoundError')
            ? 'No se detectó ningún MICRÓFONO conectado. Conecta unos auriculares/diadema con micrófono y recarga.'
            : 'No se pudo acceder al micrófono. Revisa que esté conectado y permitido.';
      }
      await enumerate();
      applySinkId(selectedSpeakerRef.current);
    })();
    navigator.mediaDevices?.addEventListener?.('devicechange', enumerate);
    return () => { mounted = false; navigator.mediaDevices?.removeEventListener?.('devicechange', enumerate); };
  }, [applySinkId]);

  // Prueba de micrófono: abre el mic elegido y publica el NIVEL de entrada (0-100)
  // para que el agente vea si capta. startMicTest/stopMicTest desde la UI de ajustes.
  const stopMicTest = useCallback(() => {
    const t = micTestRef.current;
    if (!t) return;
    try { cancelAnimationFrame(t.raf); } catch {}
    try { t.stream?.getTracks().forEach((x) => x.stop()); } catch {}
    try { t.ctx?.close(); } catch {}
    micTestRef.current = null;
    setMicLevel(0);
  }, []);

  const startMicTest = useCallback(async () => {
    try {
      stopMicTest();
      const stream = await navigator.mediaDevices.getUserMedia(buildConstraints());
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      micTestRef.current = { stream, ctx, raf: 0 };
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) { const v = Math.abs(data[i] - 128); if (v > peak) peak = v; }
        setMicLevel(Math.min(100, Math.round((peak / 128) * 100)));
        if (micTestRef.current) micTestRef.current.raf = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      console.warn('[Softphone] mic test', e);
      toast.error('No se pudo abrir el micrófono seleccionado');
    }
  }, [stopMicTest]);

  // ── Volumen de micrófono (ganancia Web Audio) con guardas ──
  // En 100% se usa la pista CRUDA del mic (no se inserta grafo): el camino por
  // defecto queda intacto. Solo al mover el slider se procesa. Si algo falla,
  // se restaura la pista cruda → la llamada nunca se queda sin micrófono.
  const teardownMicProc = useCallback(() => {
    const p = micProcRef.current;
    if (!p) return;
    try { p.ctx.close(); } catch {}
    micProcRef.current = null;
  }, []);

  const applyMicGain = useCallback(async () => {
    const su = simpleUserRef.current;
    const pc = su?.session?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
    if (!sender) return;
    const g = Math.max(0, Math.min(2, micGainRef.current / 100));
    try {
      if (g === 1) { // 100 %: pista cruda, sin grafo
        const p = micProcRef.current;
        if (p && p.rawTrack && sender.track !== p.rawTrack) await sender.replaceTrack(p.rawTrack);
        teardownMicProc();
        return;
      }
      let p = micProcRef.current;
      const rawTrack = (p && p.rawTrack) || sender.track;
      if (!p || p.rawTrack !== rawTrack) { // (re)construir grafo para esta pista
        teardownMicProc();
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        ctx.resume?.().catch(() => {});
        const source = ctx.createMediaStreamSource(new MediaStream([rawTrack]));
        const gainNode = ctx.createGain();
        const dest = ctx.createMediaStreamDestination();
        source.connect(gainNode); gainNode.connect(dest);
        p = { ctx, gainNode, dest, rawTrack };
        micProcRef.current = p;
        await sender.replaceTrack(dest.stream.getAudioTracks()[0]);
      }
      p.gainNode.gain.value = g;
    } catch (e) {
      console.warn('[Softphone] mic gain', e);
      try { const p = micProcRef.current; if (p && p.rawTrack) await sender.replaceTrack(p.rawTrack); } catch {}
    }
  }, [teardownMicProc]);

  // Aplica NS/EC/AGC a la pista viva de la llamada actual (efecto inmediato).
  const applyLiveConstraints = useCallback(() => {
    const su = simpleUserRef.current;
    const pc = su?.session?.sessionDescriptionHandler?.peerConnection;
    const sender = pc?.getSenders?.().find((s) => s.track && s.track.kind === 'audio');
    const track = micProcRef.current?.rawTrack || sender?.track;
    if (!track || typeof track.applyConstraints !== 'function') return;
    track.applyConstraints({
      echoCancellation: echoRef.current,
      noiseSuppression: noiseRef.current,
      autoGainControl: agcRef.current,
    }).catch(() => {});
  }, []);

  const setNoiseSuppression = useCallback((v) => { noiseRef.current = !!v; setNoiseState(!!v); try { localStorage.setItem('pbx-ns', v ? '1' : '0'); } catch {} applyLiveConstraints(); }, [applyLiveConstraints]);
  const setEchoCancellation = useCallback((v) => { echoRef.current = !!v; setEchoState(!!v); try { localStorage.setItem('pbx-ec', v ? '1' : '0'); } catch {} applyLiveConstraints(); }, [applyLiveConstraints]);
  const setAutoGainControl = useCallback((v) => { agcRef.current = !!v; setAgcState(!!v); try { localStorage.setItem('pbx-agc', v ? '1' : '0'); } catch {} applyLiveConstraints(); }, [applyLiveConstraints]);
  const setMicGain = useCallback((v) => { const n = Math.max(0, Math.min(200, Math.round(v))); micGainRef.current = n; setMicGainState(n); try { localStorage.setItem('pbx-mic-gain', String(n)); } catch {} applyMicGain(); }, [applyMicGain]);
  const setSpeakerVolume = useCallback((v) => { const n = Math.max(0, Math.min(100, Math.round(v))); speakerVolRef.current = n; setSpeakerVolState(n); try { localStorage.setItem('pbx-spk-vol', String(n)); } catch {} const el = remoteAudioRef.current; if (el) el.volume = n / 100; }, []);

  // ── ICE restart automático ──────────────────────────────────────────
  // Cuando el agente está detrás de una VPN/NAT simétrico o con salida
  // multi-path, la IP real de los paquetes RTP puede cambiar a MITAD de
  // llamada aunque la señalización SIP (WSS) siga viva — Asterisk deja de
  // ver tráfico del candidato ICE negociado y el peerConnection pasa a
  // 'disconnected'/'failed' sin que nadie cuelgue: la llamada queda muda
  // indefinidamente. En vez de eso, se reintenta la negociación ICE
  // (re-INVITE con iceRestart) unas pocas veces. Ver pbx-turns-tls.
  const iceRestartRef = useRef({ attempts: 0, timer: null, pc: null, listener: null });

  const resetIceRestart = useCallback(() => {
    const r = iceRestartRef.current;
    if (r.timer) clearTimeout(r.timer);
    r.attempts = 0;
    r.timer = null;
  }, []);

  const doIceRestart = useCallback(async (session) => {
    const r = iceRestartRef.current;
    if (r.attempts >= 3) {
      toast.error('Se perdió el audio y no se pudo recuperar la conexión. Cuelga y vuelve a llamar.', { id: 'ice-restart-fail', duration: 10000 });
      return;
    }
    r.attempts += 1;
    console.warn(`[Softphone] ICE restart intento ${r.attempts}`);
    try {
      await session.invite({ sessionDescriptionHandlerOptions: { offerOptions: { iceRestart: true } } });
    } catch (e) {
      console.warn('[Softphone] ICE restart falló', e);
    }
  }, []);

  const attachIceMonitor = useCallback((session) => {
    const pc = session?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    resetIceRestart();
    const onIceStateChange = () => {
      const state = pc.iceConnectionState;
      const r = iceRestartRef.current;
      if (state === 'connected' || state === 'completed') {
        if (r.timer) { clearTimeout(r.timer); r.timer = null; }
        if (r.attempts > 0) {
          toast.success('Conexión de audio restablecida');
          r.attempts = 0;
        }
      } else if (state === 'disconnected') {
        // Puede ser un corte momentáneo — espera antes de reaccionar para
        // no reiniciar ICE por un parpadeo de red que se resuelve solo.
        if (r.timer) clearTimeout(r.timer);
        r.timer = setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
            doIceRestart(session);
          }
        }, 4000);
      } else if (state === 'failed') {
        if (r.timer) clearTimeout(r.timer);
        doIceRestart(session);
      }
    };
    pc.addEventListener('iceconnectionstatechange', onIceStateChange);
    iceRestartRef.current.pc = pc;
    iceRestartRef.current.listener = onIceStateChange;
  }, [doIceRestart, resetIceRestart]);

  const detachIceMonitor = useCallback(() => {
    const r = iceRestartRef.current;
    if (r.pc && r.listener) r.pc.removeEventListener('iceconnectionstatechange', r.listener);
    resetIceRestart();
    iceRestartRef.current.pc = null;
    iceRestartRef.current.listener = null;
  }, [resetIceRestart]);

  // ── Ringback tone (tono de llamada local mientras marca) ──
  const ringbackRef = useRef(null);

  const startRingback = useCallback(() => {
    try {
      if (ringbackRef.current) return;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const cadenceOn = 1.0, cadenceOff = 4.0; // patrón tipo Colombia (1s tono / 4s silencio)
      const play = () => {
        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.15, now + 0.05);
        gain.gain.setValueAtTime(0.15, now + cadenceOn - 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + cadenceOn);
        gain.connect(ctx.destination);
        [440, 480].forEach(f => {
          const osc = ctx.createOscillator();
          osc.frequency.value = f;
          osc.connect(gain);
          osc.start(now);
          osc.stop(now + cadenceOn);
        });
      };
      play();
      const timer = setInterval(play, (cadenceOn + cadenceOff) * 1000);
      ringbackRef.current = { ctx, timer };
    } catch (e) { console.warn('[Softphone] ringback error', e); }
  }, []);

  const stopRingback = useCallback(() => {
    const s = ringbackRef.current;
    if (!s) return;
    clearInterval(s.timer);
    try { s.ctx.close(); } catch {}
    ringbackRef.current = null;
  }, []);

  useEffect(() => {
    api.get('/extensions/sip-credentials')
      .then(r => setCredentials(r.data))
      .catch(err => console.warn('[Softphone] No SIP credentials:', err?.response?.status));
  }, []);

  // El TURN ya no usa una contraseña fija publicada en el JS: el backend emite una credencial que caduca (REST de coturn).
  useEffect(() => {
    api.get('/turn/credentials')
      .then(r => setTurnCred(r.data))
      .catch(err => { console.warn('[Softphone] Sin credencial TURN:', err?.response?.status); setTurnCred(null); });
  }, []);

  useEffect(() => {
    if (!credentials?.number || !credentials?.sip_password) return;
    if (turnCred === undefined) return; // esperar a saber si hay TURN antes de crear el agente SIP

    const domain = window.location.hostname;
    // Host del servidor TURN de ESTA instancia. Se fija en build por
    // REACT_APP_TURN_HOST (p.ej. 147.93.186.79 para 1761); si no, cae al dominio
    // desde el que se sirve el panel. Nunca una IP de otra instancia.
    const turnHost = (window.__PBX_CONFIG__ && window.__PBX_CONFIG__.turnHost)
      || process.env.REACT_APP_TURN_HOST || domain;
    // Multi Company: la identidad SIP interna (lo que se registra/autentica)
    // es sip_username ('c'+company_id+'_'+number), namespaced por empresa —
    // NO credentials.number, que es solo el número que el humano ve/marca y
    // puede repetirse entre empresas. Fallback a number por si el backend
    // todavía no lo manda (instancias sin la migración de Multi Company).
    const sipUser = credentials.sip_username || credentials.number;
    const aor = `sip:${sipUser}@${domain}`;
    const wsServer = getWsServer();

    console.log('[Softphone] Connecting to', wsServer, 'as', aor);

    const su = new Web.SimpleUser(wsServer, {
      aor,
      media: { remote: { audio: remoteAudioRef.current } },
      // La sesión del agente se caía en plena llamada: durante la llamada el WSS
      // (señalización SIP) queda ocioso — el audio va por RTP aparte — y la red/NAT
      // del agente dropea el TCP → "Web socket closed abruptly" y se pierde el
      // contacto (qualify=0 a propósito no lo revive). keepAliveInterval manda un
      // ping cada 15s para que el WSS NUNCA quede ocioso.
      // OJO sip.js: session-manager solo inyecta el `server` en transportOptions si
      // NO se pasa transportOptions. Si defines transportOptions DEBES incluir el
      // server tú mismo, o el transporte queda sin server → crash (pantalla negra).
      reconnectionAttempts: 60,
      reconnectionDelay: 2,
      userAgentOptions: {
        authorizationUsername: sipUser,
        authorizationPassword: credentials.sip_password,
        displayName: credentials.caller_id_name || credentials.number,
        logLevel: 'warn',
        transportOptions: { server: wsServer, keepAliveInterval: 15 },
        sessionDescriptionHandlerFactoryOptions: {
          iceGatheringTimeout: 5000,
          peerConnectionConfiguration: {
            // Política ICE tunable vía frontend-config.js (global) o por el backend
            // según la IP del agente (credentials.iceTransportPolicy, oficinas con NAT
            // simétrico conocido — ver FORCE_RELAY_IPS en extensions.js). Por defecto
            // NO se fija (ICE elige; prefiere la ruta directa, que es la que funciona
            // para la mayoría). OJO (jul-2026): forzar 'relay' GLOBAL se PROBÓ y se
            // REVIRTIÓ — el relay local apenas cursaba media (~16 pkt/18s) → llamadas
            // mudas. Ver [[pbx-frontend-backend-gotchas]].
            ...(credentials.iceTransportPolicy || (window.__PBX_CONFIG__ && window.__PBX_CONFIG__.iceTransportPolicy)
              ? { iceTransportPolicy: credentials.iceTransportPolicy || window.__PBX_CONFIG__.iceTransportPolicy }
              : {}),
            // El TURN debe ser el de LA PROPIA instancia (mismo host que su
            // Asterisk), no una IP fija: antes estaba hardcodeado a 209.126.0.233
            // (coturn de 1223), así que los agentes de 1761 intentaban relayar
            // por el coturn de OTRO servidor → sin audio. `domain` es el dominio
            // desde el que se sirve el panel (p.ej. 1761.808pbx.cloud → 147.x).
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' },
              ...(turnCred ? [
                { urls: `turn:${turnHost}:3478?transport=udp`, username: turnCred.username, credential: turnCred.credential },
                { urls: `turn:${turnHost}:3478?transport=tcp`, username: turnCred.username, credential: turnCred.credential },
              ] : []),
              // TURNS (TLS) en 5349: candidato extra para agentes detrás de VPN/
              // firewall corporativo que bloquea UDP/TCP "crudo" en 3478 por DPI
              // aunque el puerto esté abierto. El navegador prueba todos los
              // candidatos y usa el que sí conecte — no cambia el comportamiento
              // de nadie más (sep-2026, ver [[pbx-turns-tls]]).
              ...(turnCred ? [
                { urls: `turns:${turnHost}:5349?transport=tcp`, username: turnCred.username, credential: turnCred.credential },
              ] : []),
            ],
          },
        },
      },
      delegate: {
        onCallReceived: async () => {
          const rid = su.session?.remoteIdentity;
          const dispName = rid?.displayName || '';
          const fromUser = rid?.uri?.user || 'Desconocido';

          // Llamada de MONITOREO (spy/whisper/barge): auto-contestar y reproducir
          // en el panel, sin timbrar ni pedir contestar. La marca la pone el backend
          // en el callerID ("MON Escuchar/Susurrar/Irrumpir").
          if (/^MON\b/i.test(dispName)) {
            try {
              await su.answer({ sessionDescriptionHandlerOptions: { constraints: buildConstraints() } });
              setCallState('active');
              setCurrentCall({
                number: dispName.replace(/^MON\s*/i, '') || 'Monitoreo',
                direction: 'monitor',
                monitoring: true,
                startedAt: Date.now(),
              });
            } catch (e) { console.warn('[Softphone] auto-answer monitor error', e); }
            return;
          }

          setCallState('ringing');
          setCurrentCall({
            number: fromUser,
            direction: 'inbound',
            startedAt: null,
          });
        },
        onCallAnswered: () => {
          setCallState('active');
          setCurrentCall(c => ({ ...c, startedAt: Date.now() }));
          // aplicar ajustes del agente a la llamada recién activa
          applyMicGain();
          const el = remoteAudioRef.current; if (el) el.volume = speakerVolRef.current / 100;
          if (su.session) attachIceMonitor(su.session);
        },
        onCallHangup: () => {
          setCallState('idle');
          setCurrentCall(null);
          setIsMuted(false);
          setIsHeld(false);
          teardownMicProc();
          detachIceMonitor();
        },
        onCallHold: (held) => {
          setIsHeld(held);
          setCallState(held ? 'held' : 'active');
        },
        onRegistered: () => {
          console.log('[Softphone] Registered successfully');
          setRegState('registered');
        },
        onUnregistered: () => {
          console.log('[Softphone] Unregistered');
          setRegState('unregistered');
        },
        onServerConnect: () => {
          console.log('[Softphone] WS connected, registering...');
          setRegState('registering');
        },
        onServerDisconnect: (err) => {
          console.warn('[Softphone] WS disconnected', err);
          setRegState('error');
        },
      },
    });

    simpleUserRef.current = su;

    su.connect()
      .then(() => {
        console.log('[Softphone] connect() resolved, calling register()');
        return su.register();
      })
      .then(() => {
        console.log('[Softphone] register() resolved - setting registered');
        setRegState('registered');
      })
      .catch(err => {
        console.error('[Softphone] connect/register error:', err);
        setRegState('error');
      });

    return () => {
      console.log('[Softphone] Cleanup: disconnecting');
      detachIceMonitor();
      su.disconnect().catch(() => {});
      simpleUserRef.current = null;
    };
  }, [credentials, turnCred, detachIceMonitor]);

  // Enciende/apaga el tono de llamada según el estado
  useEffect(() => {
    if (callState === 'calling') startRingback();
    else stopRingback();
    return () => stopRingback();
  }, [callState, startRingback, stopRingback]);

  // Notificaciones de progreso de llamada (Asterisk vía WebSocket del backend)
  useEffect(() => {
    const myExt = credentials?.number;
    if (!myExt) return undefined;
    const off = pbxWS.on('call.progress', (data) => {
      if (!data || String(data.extension) !== String(myExt)) return;
      setCallProgress({ disposition: data.disposition, statusText: data.statusText, final: !!data.final });
      if (data.final) {
        const alertable = ['busy', 'voicemail', 'unavailable', 'congestion', 'noanswer', 'cancel'];
        if (alertable.includes(data.disposition)) {
          toast(data.statusText, { icon: data.disposition === 'voicemail' ? '📩' : '⚠️' });
        }
      }
    });
    return off;
  }, [credentials]);

  // Limpiar el progreso al volver a reposo
  useEffect(() => {
    if (callState === 'idle') setCallProgress(null);
  }, [callState]);

  // Señal para useIdleLogout: mientras NO estemos en reposo hay una llamada en
  // curso (llamando/timbrando/activa/en espera) → el auto-logout no debe cortarla.
  useEffect(() => {
    try {
      if (callState === 'idle') localStorage.removeItem('pbx-on-call');
      else localStorage.setItem('pbx-on-call', String(Date.now()));
    } catch (_) {}
  }, [callState]);

  const call = useCallback(async (number) => {
    if (!simpleUserRef.current || callState !== 'idle') return;
    const domain = window.location.hostname;
    try {
      setCallState('calling');
      setCurrentCall({ number, direction: 'outbound', startedAt: null });
      await simpleUserRef.current.call(`sip:${number}@${domain}`, {
        sessionDescriptionHandlerOptions: { constraints: buildConstraints() },
        // Audio temprano: sin esto SIP.js descarta el 183+SDP y el agente NO oye
        // lo que el operador reproduce antes de contestar ("número no disponible",
        // "ocupado", buzón, tono real de la red), solo el tono local sintético.
        earlyMedia: true,
      }, {
        requestDelegate: {
          // Si el operador manda audio (183 con SDP) se apaga el tono local para no
          // pisarlo; si solo manda 180 (sin audio) el tono local sigue como antes.
          onProgress: (resp) => {
            if (resp?.message?.statusCode === 183 && resp.message.body) stopRingback();
          },
        },
      });
    } catch (err) {
      console.error('[Softphone] call error:', err);
      setCallState('idle');
      setCurrentCall(null);
    }
  }, [callState, stopRingback]);

  const answer = useCallback(async () => {
    if (!simpleUserRef.current) return;
    await simpleUserRef.current.answer({
      sessionDescriptionHandlerOptions: { constraints: buildConstraints() },
    });
  }, []);

  const hangup = useCallback(async () => {
    if (!simpleUserRef.current) return;
    if (secondSessionRef.current) {
      const s = secondSessionRef.current;
      secondSessionRef.current = null;
      teardownMix();
      setSecondCall(null);
      try { (s.state === SessionState.Established ? s.bye() : s.cancel()); } catch {}
    }
    try { await simpleUserRef.current.hangup(); } catch {}
    setCallState('idle');
    setCurrentCall(null);
    setIsMuted(false);
    setIsHeld(false);
  }, []);

  const mute = useCallback(() => {
    if (!simpleUserRef.current) return;
    if (isMuted) { simpleUserRef.current.unmute(); setIsMuted(false); }
    else { simpleUserRef.current.mute(); setIsMuted(true); }
  }, [isMuted]);

  const hold = useCallback(async () => {
    if (!simpleUserRef.current) return;
    if (isHeld) await simpleUserRef.current.unhold();
    else await simpleUserRef.current.hold();
  }, [isHeld]);

  const sendDTMF = useCallback(async (tone) => {
    if (!simpleUserRef.current || callState !== 'active') return;
    await simpleUserRef.current.sendDTMF(tone);
  }, [callState]);

  // ── Transferencia ciega (REFER) ────────────────────────────
  // Mismo patrón ya probado en el softphone de escritorio (renderer.js):
  // REFER directo por SIP.js, sin pasar por el backend. Tras el REFER
  // colgamos nuestra pierna; Asterisk conecta al destino con el que llamaba.
  const transfer = useCallback(async (destination) => {
    if (!simpleUserRef.current?.session || callState !== 'active') return;
    const dest = String(destination || '').trim().replace(/[^0-9+*#]/g, '');
    if (!dest) return;
    const domain = window.location.hostname;
    try {
      const targetUri = UserAgent.makeURI(`sip:${dest}@${domain}`);
      if (!targetUri) throw new Error('destino inválido');
      await simpleUserRef.current.session.refer(targetUri);
      toast.success(`Transferido a ${dest}`);
      setTimeout(() => { hangup().catch(() => {}); }, 800);
    } catch (err) {
      console.error('[Softphone] transfer error:', err);
      toast.error('No se pudo transferir: ' + (err.message || err));
    }
  }, [callState]);

  // ── Conferencia 3 vías ──────────────────────────────────────
  // Segunda llamada SALIENTE sobre el MISMO UserAgent ya registrado (Call A no
  // se toca: no hace falta registrar dos veces, lo que rompería max_contacts=1
  // WebRTC del endpoint). Hereda el mismo iceServers/TURN/relay de Call A porque
  // sip.js lee esa config del UserAgent compartido, no por sesión.
  // Mezcla de audio LOCAL (Web Audio API): en vez de pasar por Asterisk, el
  // navegador mezcla mic+remoto-B hacia Call A, mic+remoto-A hacia Call B, y
  // remoto-A+remoto-B hacia lo que oye el agente. Así los 3 se oyen entre sí
  // sin tocar la ruta de medios ya endurecida hoy (rtp_symmetric, relay, etc.).
  const teardownMix = useCallback(() => {
    const mix = audioMixRef.current;
    if (!mix) return;
    try {
      if (mix.callASender && mix.callAOriginalTrack) mix.callASender.replaceTrack(mix.callAOriginalTrack);
    } catch (e) { console.warn('[Conference] restore Call A track', e); }
    try { remoteAudioRef.current.srcObject = simpleUserRef.current?.remoteMediaStream || null; } catch {}
    try { mix.ctx.close(); } catch {}
    audioMixRef.current = null;
  }, []);

  const engageMix = useCallback(() => {
    try {
      const suA = simpleUserRef.current;
      const sessionB = secondSessionRef.current;
      const pcA = suA?.session?.sessionDescriptionHandler?.peerConnection;
      const pcB = sessionB?.sessionDescriptionHandler?.peerConnection;
      const localA = suA?.localMediaStream;
      const remoteA = suA?.remoteMediaStream;
      const remoteB = sessionB?.sessionDescriptionHandler?.remoteMediaStream;
      if (!pcA || !pcB || !localA || !remoteA || !remoteB) {
        throw new Error('media no disponible todavía');
      }
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const micSrc = ctx.createMediaStreamSource(localA);
      const remoteASrc = ctx.createMediaStreamSource(remoteA);
      const remoteBSrc = ctx.createMediaStreamSource(remoteB);

      const destForA = ctx.createMediaStreamDestination();   // lo que oye X: mic + Y
      micSrc.connect(destForA); remoteBSrc.connect(destForA);

      const destForB = ctx.createMediaStreamDestination();   // lo que oye Y: mic + X
      micSrc.connect(destForB); remoteASrc.connect(destForB);

      const destForLocal = ctx.createMediaStreamDestination(); // lo que oye el agente: X + Y
      remoteASrc.connect(destForLocal); remoteBSrc.connect(destForLocal);

      const callASender = pcA.getSenders().find(s => s.track?.kind === 'audio');
      const callBSender = pcB.getSenders().find(s => s.track?.kind === 'audio');
      const callAOriginalTrack = callASender?.track || null;
      callASender?.replaceTrack(destForA.stream.getAudioTracks()[0]);
      callBSender?.replaceTrack(destForB.stream.getAudioTracks()[0]);
      remoteAudioRef.current.srcObject = destForLocal.stream;

      audioMixRef.current = { ctx, callASender, callAOriginalTrack };
    } catch (e) {
      console.error('[Conference] engageMix error:', e);
      toast.error('No se pudo unir el audio de la conferencia');
    }
  }, []);

  const endConference = useCallback(async () => {
    const s = secondSessionRef.current;
    secondSessionRef.current = null;
    teardownMix();
    setSecondCall(null);
    if (!s) return;
    try {
      if (s.state === SessionState.Established) await s.bye();
      else await s.cancel();
    } catch (e) { console.warn('[Conference] endConference hangup B', e); }
  }, [teardownMix]);

  const startConference = useCallback(async (number) => {
    if (!simpleUserRef.current?.session || callState !== 'active' || secondSessionRef.current) return;
    const dest = String(number || '').trim().replace(/[^0-9+*#]/g, '');
    if (!dest) return;
    const domain = window.location.hostname;
    try {
      const userAgent = simpleUserRef.current.sessionManager.userAgent;
      const targetUri = UserAgent.makeURI(`sip:${dest}@${domain}`);
      if (!targetUri) throw new Error('destino inválido');
      const inviter = new Inviter(userAgent, targetUri, {
        sessionDescriptionHandlerOptions: { constraints: buildConstraints() },
      });
      secondSessionRef.current = inviter;
      setSecondCall({ number: dest, state: 'calling' });

      const onStateChange = (state) => {
        if (secondSessionRef.current !== inviter) return; // sesión ya reemplazada/cerrada
        if (state === SessionState.Established) {
          setSecondCall({ number: dest, state: 'active' });
          engageMix();
        } else if (state === SessionState.Terminated) {
          inviter.stateChange.removeListener(onStateChange);
          secondSessionRef.current = null;
          teardownMix();
          setSecondCall(null);
        }
      };
      inviter.stateChange.addListener(onStateChange);

      await inviter.invite();
    } catch (err) {
      console.error('[Softphone] startConference error:', err);
      toast.error('No se pudo agregar la llamada: ' + (err.message || err));
      secondSessionRef.current = null;
      setSecondCall(null);
    }
  }, [callState, engageMix, teardownMix]);

  const value = {
    regState, callState, currentCall, isMuted, isHeld, credentials, callProgress,
    remoteAudioRef, call, answer, hangup, mute, hold, sendDTMF, transfer,
    // Conferencia 3 vías
    secondCall, startConference, endConference,
    // Hardware de audio
    audioDevices, selectedMic, selectedSpeaker, setSelectedMic, setSelectedSpeaker,
    micLevel, startMicTest, stopMicTest, audioError,
    // Procesamiento de audio (ruido/eco/AGC + volúmenes)
    noiseSuppression, echoCancellation, autoGainControl, micGain, speakerVolume,
    setNoiseSuppression, setEchoCancellation, setAutoGainControl, setMicGain, setSpeakerVolume,
  };

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
    </SoftphoneContext.Provider>
  );
}

export function useSoftphone() {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) throw new Error('useSoftphone must be used inside SoftphoneProvider');
  return ctx;
}
